import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Country } from "react-phone-number-input";
import * as yup from "yup";

import Button, { Color } from "../../../../../components/Form/Button";
import Input from "../../../../../components/Form/Input";
import PhoneInput from "../../../../../components/Form/PhoneInput";
import useYupValidation from "../../../../../hooks/useYupValidation";

import { validateFirstStep } from "./validation";
import * as S from "./styles";
import { SIGNUP_COLORS } from "../../tokens";
import {
  CompanyInfoLabel,
  CompanySlugContainer,
  CompanySlugInfo,
  CompanySlugSubdomain,
  CompanySlugUrl,
} from "./styles";

export type FirstStepForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country?: string;
  subdomain?: string;
};

interface FirstStepProp {
  setNextStep?: () => void;
  objectPreview?: FirstStepForm;
}

export interface FirstStepHandle {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country?: Country;
  subdomain?: string;
}

const FirstStep: ForwardRefRenderFunction<FirstStepHandle, FirstStepProp> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { setNextStep, objectPreview } = props;

  const [firstName, setFirstName] = useState<string>(objectPreview?.firstName ?? "");
  const [lastName, setLastName] = useState<string>(objectPreview?.lastName ?? "");
  const [email, setEmail] = useState<string>(objectPreview?.email ?? "");
  const [phone, setPhone] = useState<string>(objectPreview?.phone ?? "");
  const [country, setCountry] = useState<Country>((objectPreview?.country as Country) ?? "AU");
  const [subdomain, setSubdomain] = useState<string>(objectPreview?.subdomain ?? "");

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const formValues: FirstStepHandle = useMemo(
    () => ({ firstName, lastName, email, phone, country, subdomain }),
    [firstName, lastName, email, phone, country, subdomain]
  );

  useImperativeHandle(ref, () => formValues);

  const canProceed = useMemo(() => {
    if (!firstName || !lastName || !email || !phone || !country) return false;
    const silent = (_e: yup.ValidationError[]) => void 0;
    try {
      return validateFirstStep(translate, formValues, silent) === true;
    } catch {
      return false;
    }
  }, [firstName, lastName, email, phone, country, translate, formValues]);

  const handleNextStep = () => {
    setIsSubmitted(true);
    if (validateFirstStep(translate, formValues, setErrors)) {
      setNextStep?.();
    }
  };

  useEffect(() => {
    if (objectPreview) {
      setFirstName(objectPreview.firstName ?? "");
      setLastName(objectPreview.lastName ?? "");
      setEmail(objectPreview.email ?? "");
      setPhone(objectPreview.phone ?? "");
      setCountry((objectPreview.country as Country) ?? "AU");
      setSubdomain(objectPreview.subdomain ?? "");
    }
  }, [objectPreview]);
  useEffect(() => {
    if (isSubmitted) validateFirstStep(translate, formValues, setErrors);
  }, [isSubmitted, firstName, lastName, email, phone, country, translate, formValues]);

  const fieldBaseInfo = (name: string, customPlaceholder?: string) =>
    ({
      error: fieldHasError(name),
      errorMessage: getErrorByField(name),
      "data-cy": name,
      label: translate(name),
      placeholder: customPlaceholder || translate(name),
      "aria-label": name,
      name,
      bordercolor: SIGNUP_COLORS.primary,
    } as const);

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} md={6} sx={{ mb: "24px", pr: { md: 1.5 } }}>
        <Input
          {...fieldBaseInfo("firstName")}
          required
          onChange={(event) => setFirstName(event.target.value)}
          value={firstName}
          sx={S.inputSx}
        />
      </Grid>

      <Grid item xs={12} md={6} sx={{ mb: "24px", pl: { md: 1.5 } }}>
        <Input
          {...fieldBaseInfo("lastName")}
          required
          onChange={(event) => setLastName(event.target.value)}
          value={lastName}
          sx={S.inputSx}
        />
      </Grid>

      <Grid item xs={12} sx={S.gridItem}>
        <Input
          {...fieldBaseInfo("email", translate("sampleEmailCom"))}
          required
          onChange={(event) => setEmail(event.target.value)}
          value={email}
          type="email"
          inputMode="email"
          sx={S.inputSx}
        />
      </Grid>

      <Grid item xs={12} sx={S.gridItem}>
        <PhoneInput
          {...fieldBaseInfo("phoneNumber")}
          required
          international
          defaultCountry="AU"
          onChange={(value) => setPhone(value || "")}
          placeholder="+61 0 0000 0000"
          value={phone}
          onCountryChange={(c: Country) => setCountry(c)}
        />
      </Grid>

      <Grid item xs={12} sx={{ mb: 0 }}>
        <CompanySlugContainer>
          <CompanyInfoLabel>{translate("customerPortalAddress")}</CompanyInfoLabel>
          <CompanySlugInfo>{translate("definesLinkYourCustomerWillUse")}</CompanySlugInfo>
          <CompanySlugSubdomain>
            <CompanySlugUrl>https://</CompanySlugUrl>
            <Input
              name="subdomain"
              data-cy="subdomain"
              value={subdomain}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target;
                const formattedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, "");

                setSubdomain(formattedValue);
              }}
            />
            <CompanySlugUrl>.shipeezi.com</CompanySlugUrl>
          </CompanySlugSubdomain>
        </CompanySlugContainer>
      </Grid>

      <Grid item xs={12} sx={{ mt: "16px" }}>
        <Button
          fullWidth
          disabled={!canProceed}
          customColor={Color.SECONDARY_DARK}
          data-cy="nextButton"
          aria-label="nextButton"
          onClick={handleNextStep}
        >
          {translate("next")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default forwardRef(FirstStep);
